import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Star, 
  Calendar, 
  DollarSign, 
  Briefcase,
  Phone,
  Eye,
  Search,
  Award,
  Users,
  Store,
  Plus,
  Building2,
  CheckCircle,
  SlidersHorizontal,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import ContactModal from '../components/ContactModal';
import Navigation from '../components/Navigation';
import ProfessionalFilters from '../components/ProfessionalFilters';
import Pagination from '../components/Pagination';
import ProfessionalCard from '../components/ProfessionalCard';

const DepartmentDetailPage = () => {
  const { state, city, department } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profiles');
  const [profilesSubTab, setProfilesSubTab] = useState('professionals');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    experience: 'all',
    availability: 'all',
    rateRange: 'all',
    specialization: 'all',
    sortBy: 'rating',
    hasPortfolio: 'all'
  });
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Contact modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactRecipient, setContactRecipient] = useState(null);
  
  // Loading states for API calls
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Fetch professionals from backend
  const fetchProfessionals = useCallback(async () => {
    setLoadingProfessionals(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        department: department,
        state: state,
        city: city
      });
      
      const response = await fetch(`${backendUrl}/api/users?${params}`);
      if (response.ok) {
        const data = await response.json();
        setApiProfessionals(data);
        setFilteredProfessionals(data);
      } else {
        console.error('Failed to fetch professionals');
        setApiProfessionals([]);
        setFilteredProfessionals([]);
      }
    } catch (error) {
      console.error('Error fetching professionals:', error);
      setApiProfessionals([]);
      setFilteredProfessionals([]);
    } finally {
      setLoadingProfessionals(false);
    }
  }, [department, state, city]);
  
  // Fetch vendors from backend
  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        department: department,
        city: city
      });
      
      const response = await fetch(`${backendUrl}/api/vendors?${params}`);
      if (response.ok) {
        const data = await response.json();
        setApiVendors(data);
        setFilteredVendors(data);
      } else {
        console.error('Failed to fetch vendors');
        setApiVendors([]);
        setFilteredVendors([]);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setApiVendors([]);
      setFilteredVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, [department, city]);
  
  // Fetch job posts from backend
  const fetchJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        department: department,
        state: state,
        city: city
      });
      
      const response = await fetch(`${backendUrl}/api/projects?${params}`);
      if (response.ok) {
        const data = await response.json();
        setApiJobs(data);
        setFilteredJobs(data);
      } else {
        console.error('Failed to fetch jobs');
        setApiJobs([]);
        setFilteredJobs([]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setApiJobs([]);
      setFilteredJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  }, [department, state, city]);
  
  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchProfessionals();
    fetchVendors();
    fetchJobs();
  }, [fetchProfessionals, fetchVendors, fetchJobs]);

  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only activate if no input/textarea is focused and pagination is visible
      if (document.activeElement.tagName === 'INPUT' || 
          document.activeElement.tagName === 'TEXTAREA' ||
          contactModalOpen) {
        return;
      }

      const totalItems = activeTab === 'profiles' && profilesSubTab === 'professionals' 
        ? filteredProfessionals.length 
        : activeTab === 'jobs' 
        ? filteredJobs.length 
        : filteredVendors.length;
      
      const totalPages = Math.ceil(totalItems / itemsPerPage);

      if (e.key === 'ArrowLeft' && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (e.key === 'ArrowRight' && currentPage < totalPages) {
        setCurrentPage(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, activeTab, profilesSubTab, filteredProfessionals.length, filteredJobs.length, filteredVendors.length, itemsPerPage, contactModalOpen]);

  const specializations = ['Art Director', 'Set Designer', 'Prop Master', 'Scenic Artist'];
  
  // Store original API data for filtering
  const [apiProfessionals, setApiProfessionals] = useState([]);
  const [apiVendors, setApiVendors] = useState([]);
  const [apiJobs, setApiJobs] = useState([]);
  
  // Vendor form state
  const [vendorFormData, setVendorFormData] = useState({
    name: '',
    category: '',
    location: '',
    phone: '',
    email: '',
    description: '',
    services: ''
  });

  // OLD MOCK DATA INITIALIZATION REMOVED - Now using API data

  // Wrap in useCallback to prevent recreation and move BEFORE the useEffect that depends on it
  const applyProfessionalFilters = useCallback(() => {
    let filtered = [...apiProfessionals];

    if (searchQuery) {
      filtered = filtered.filter(prof => 
        prof.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prof.designation.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prof.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filters.experience !== 'all') {
      filtered = filtered.filter(prof => {
        if (filters.experience === 'junior') return prof.experience < 5;
        if (filters.experience === 'mid') return prof.experience >= 5 && prof.experience <= 10;
        if (filters.experience === 'senior') return prof.experience > 10;
        return true;
      });
    }

    if (filters.availability !== 'all') {
      filtered = filtered.filter(prof => prof.availability === filters.availability);
    }

    if (filters.rateRange !== 'all') {
      filtered = filtered.filter(prof => {
        // Parse rate if it's a string, or skip filter if rate is not set
        const rateValue = prof.rate ? parseFloat(prof.rate.replace(/[^\d.]/g, '')) : null;
        if (!rateValue) return true; // Include professionals without rate set
        
        if (filters.rateRange === 'budget') return rateValue < 10000;
        if (filters.rateRange === 'mid') return rateValue >= 10000 && rateValue <= 15000;
        if (filters.rateRange === 'premium') return rateValue > 15000;
        return true;
      });
    }

    if (filters.specialization !== 'all') {
      filtered = filtered.filter(prof => prof.designation === filters.specialization);
    }

    // Portfolio filter
    if (filters.hasPortfolio === 'yes') {
      filtered = filtered.filter(prof => prof.portfolio && prof.portfolio.length > 0);
    } else if (filters.hasPortfolio === 'no') {
      filtered = filtered.filter(prof => !prof.portfolio || prof.portfolio.length === 0);
    }

    if (filters.sortBy === 'rating') {
      filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (filters.sortBy === 'experience') {
      filtered.sort((a, b) => (b.experience || 0) - (a.experience || 0));
    } else if (filters.sortBy === 'portfolio') {
      filtered.sort((a, b) => {
        const portfolioA = (a.portfolio || []).length;
        const portfolioB = (b.portfolio || []).length;
        return portfolioB - portfolioA;
      });
    } else if (filters.sortBy === 'rate_low') {
      filtered.sort((a, b) => {
        const rateA = a.rate ? parseFloat(a.rate.replace(/[^\d.]/g, '')) : 999999;
        const rateB = b.rate ? parseFloat(b.rate.replace(/[^\d.]/g, '')) : 999999;
        return rateA - rateB;
      });
    } else if (filters.sortBy === 'rate_high') {
      filtered.sort((a, b) => {
        const rateA = a.rate ? parseFloat(a.rate.replace(/[^\d.]/g, '')) : 0;
        const rateB = b.rate ? parseFloat(b.rate.replace(/[^\d.]/g, '')) : 0;
        return rateB - rateA;
      });
    }

    setFilteredProfessionals(filtered);
  }, [apiProfessionals, searchQuery, filters]);

  // Apply filters when search or filters change
  useEffect(() => {
    if (activeTab === 'profiles' && profilesSubTab === 'professionals') {
      applyProfessionalFilters();
    }
  }, [searchQuery, filters, profilesSubTab, activeTab, applyProfessionalFilters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const getAvailabilityBadge = (availability) => {
    const badges = {
      available: { text: 'Available Now', className: 'bg-green-100 text-green-700 border-green-200' },
      within_week: { text: 'Within 1 Week', className: 'bg-blue-100 text-blue-700 border-blue-200' },
      within_month: { text: 'Within 1 Month', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' }
    };
    return badges[availability] || badges.available;
  };

  const handleViewProfile = (professional) => {
    navigate(`/profile/${professional.user_id}`);
  };

  const handleContact = (professional) => {
    setContactRecipient(professional);
    setContactModalOpen(true);
  };

  const handleApplyJob = (job) => {
    toast.success(`Applying to: ${job.title}`);
  };

  const handleContactVendor = (vendor) => {
    setContactRecipient(vendor);
    setContactModalOpen(true);
  };

  const handleVendorSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you! Vendor submitted for review.');
    setVendorFormData({
      name: '',
      category: '',
      location: '',
      phone: '',
      email: '',
      description: '',
      services: ''
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <Navigation showBackButton={true} backTo={`/departments/${state}/${city}`} />

      {/* Header Section */}
      <section className="py-12 bg-gradient-to-b from-orange-ultralight to-background border-b border-border">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl mx-auto"
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{city}, {state}</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-foreground capitalize">
              {department?.replace(/-/g, ' ')}
            </h2>
            <p className="text-lg text-muted-foreground">
              Find professionals, vendors, and job opportunities
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Tabs */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-7xl mx-auto">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 mb-8 h-12 bg-muted p-1">
              <TabsTrigger 
                value="profiles" 
                className="flex items-center gap-2 text-base data-[state=active]:bg-accent data-[state=active]:text-white"
              >
                <Users className="w-5 h-5" />
                Profiles
              </TabsTrigger>
              <TabsTrigger 
                value="vendors" 
                className="flex items-center gap-2 text-base data-[state=active]:bg-accent data-[state=active]:text-white"
              >
                <Store className="w-5 h-5" />
                Vendors
              </TabsTrigger>
              {department === 'art-department' && (
                <TabsTrigger 
                  value="prop-shops" 
                  className="flex items-center gap-2 text-base data-[state=active]:bg-accent data-[state=active]:text-white"
                >
                  <Store className="w-5 h-5" />
                  Prop Shops
                </TabsTrigger>
              )}
            </TabsList>

            {/* PROFILES TAB */}
            <TabsContent value="profiles">
              <Tabs value={profilesSubTab} onValueChange={setProfilesSubTab}>
                <TabsList className="mb-6">
                  <TabsTrigger value="professionals">Browse Professionals</TabsTrigger>
                  <TabsTrigger value="jobs">Job Posts</TabsTrigger>
                </TabsList>

                {/* Professionals Sub-tab */}
                <TabsContent value="professionals">
                  <div className="mb-6">
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Search professionals by name, designation, or skills..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-12 h-12 bg-card border-border"
                      />
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-4 gap-6">
                    {/* Filters Sidebar */}
                    <Card className="bg-card border-border lg:col-span-1 h-fit sticky top-24">
                      <CardHeader className="pb-4">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-foreground flex items-center gap-2">
                            <SlidersHorizontal className="w-4 h-4 text-accent" />
                            Filters
                          </h3>
                          <Button variant="ghost" size="sm" onClick={() => setFilters({
                            experience: 'all', availability: 'all', rateRange: 'all',
                            specialization: 'all', sortBy: 'rating'
                          })} className="text-xs text-accent">
                            Reset
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Experience</label>
                          <Select value={filters.experience} onValueChange={(v) => handleFilterChange('experience', v)}>
                            <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Levels</SelectItem>
                              <SelectItem value="junior">Junior (0-5 yrs)</SelectItem>
                              <SelectItem value="mid">Mid (5-10 yrs)</SelectItem>
                              <SelectItem value="senior">Senior (10+ yrs)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Separator />
                        <div>
                          <label className="text-sm font-medium mb-2 block">Availability</label>
                          <Select value={filters.availability} onValueChange={(v) => handleFilterChange('availability', v)}>
                            <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="available">Available Now</SelectItem>
                              <SelectItem value="within_week">Within 1 Week</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Separator />
                        <div>
                          <label className="text-sm font-medium mb-2 block">Rate Range</label>
                          <Select value={filters.rateRange} onValueChange={(v) => handleFilterChange('rateRange', v)}>
                            <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Rates</SelectItem>
                              <SelectItem value="budget">Budget (&lt;₹10K)</SelectItem>
                              <SelectItem value="mid">Mid (₹10K-15K)</SelectItem>
                              <SelectItem value="premium">Premium (&gt;₹15K)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Separator />
                        <div>
                          <label className="text-sm font-medium mb-2 block">Portfolio</label>
                          <Select value={filters.hasPortfolio || 'all'} onValueChange={(v) => handleFilterChange('hasPortfolio', v)}>
                            <SelectTrigger className="bg-background"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All Professionals</SelectItem>
                              <SelectItem value="yes">With Portfolio</SelectItem>
                              <SelectItem value="no">Without Portfolio</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Professionals List */}
                    <div className="lg:col-span-3 space-y-6">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-muted-foreground">
                          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredProfessionals.length)}-{Math.min(currentPage * itemsPerPage, filteredProfessionals.length)} of {filteredProfessionals.length} professionals
                        </p>
                        <Select value={filters.sortBy} onValueChange={(v) => handleFilterChange('sortBy', v)}>
                          <SelectTrigger className="w-48">
                            <SelectValue placeholder="Sort by..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="rating">Highest Rated</SelectItem>
                            <SelectItem value="experience">Most Experienced</SelectItem>
                            <SelectItem value="portfolio">Most Portfolio</SelectItem>
                            <SelectItem value="rate_low">Rate: Low to High</SelectItem>
                            <SelectItem value="rate_high">Rate: High to Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {filteredProfessionals.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((prof, idx) => (
                        <Card key={prof.user_id || prof.id} className="bg-card border-border hover:border-accent/50 transition-all">
                          <CardContent className="p-6">
                            <div className="flex gap-6">
                              <div className="flex-shrink-0">
                                {prof.portfolio && prof.portfolio.length > 0 ? (
                                  <div className="relative">
                                    <img
                                      src={`${process.env.REACT_APP_BACKEND_URL}${prof.portfolio[0].image_url}`}
                                      alt={`${prof.name} portfolio`}
                                      className="w-24 h-24 rounded-lg object-cover border border-border"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="w-24 h-24 rounded-lg bg-accent/10 hidden items-center justify-center text-3xl font-bold text-accent">
                                      {prof.name.charAt(0)}
                                    </div>
                                    {prof.portfolio.length > 1 && (
                                      <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                                        +{prof.portfolio.length - 1}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center text-3xl font-bold text-accent">
                                    {prof.name.charAt(0)}
                                  </div>
                                )}
                                {prof.verified && (
                                  <Badge className="mt-2 bg-accent/10 text-accent border-accent/20">
                                    <Award className="w-3 h-3 mr-1" />Verified
                                  </Badge>
                                )}
                                {prof.portfolio && prof.portfolio.length > 0 && (
                                  <Badge className="mt-2 bg-blue-50 text-blue-600 border-blue-200">
                                    <Eye className="w-3 h-3 mr-1" />{prof.portfolio.length} Work{prof.portfolio.length > 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex justify-between mb-2">
                                  <div>
                                    <h3 className="text-xl font-semibold">{prof.name}</h3>
                                    <p className="text-accent font-medium">{prof.designation}</p>
                                    <div className="flex gap-3 text-sm text-muted-foreground mt-1">
                                      <span><Briefcase className="w-3 h-3 inline" /> {prof.experience} yrs</span>
                                      <span><MapPin className="w-3 h-3 inline" /> {prof.location?.city || prof.city}</span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <Badge className={getAvailabilityBadge(prof.availability).className + ' mt-2'}>
                                      {getAvailabilityBadge(prof.availability).text}
                                    </Badge>
                                  </div>
                                </div>
                                {prof.bio && <p className="text-sm text-muted-foreground mb-3">{prof.bio}</p>}
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {(prof.skills || []).map(skill => (
                                    <Badge key={skill} variant="secondary">{skill}</Badge>
                                  ))}
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t">
                                  <div>
                                    {prof.rate ? (
                                      <>
                                        <span className="text-lg font-semibold">{prof.rate}</span>
                                        <span className="text-sm text-muted-foreground">/day</span>
                                      </>
                                    ) : (
                                      <span className="text-sm text-muted-foreground">Rate on request</span>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => handleViewProfile(prof)}>
                                      <Eye className="w-4 h-4 mr-1" />View Profile
                                    </Button>
                                    <Button size="sm" className="bg-accent hover:bg-accent/90" onClick={() => handleContact(prof)}>
                                      <MessageSquare className="w-4 h-4 mr-1" />Contact
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      
                      {/* Pagination Controls */}
                      {filteredProfessionals.length > itemsPerPage && (
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-8">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCurrentPage(p => Math.max(1, p - 1));
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={currentPage === 1}
                            className="w-full sm:w-auto"
                          >
                            Previous
                          </Button>
                          <div className="flex gap-1 flex-wrap justify-center">
                            {Array.from({ length: Math.ceil(filteredProfessionals.length / itemsPerPage) }, (_, i) => i + 1).map(page => (
                              <Button
                                key={page}
                                variant={currentPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  setCurrentPage(page);
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }}
                                className={currentPage === page ? "bg-accent text-white" : ""}
                              >
                                {page}
                              </Button>
                            ))}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setCurrentPage(p => Math.min(Math.ceil(filteredProfessionals.length / itemsPerPage), p + 1));
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            disabled={currentPage === Math.ceil(filteredProfessionals.length / itemsPerPage)}
                            className="w-full sm:w-auto"
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Job Posts Sub-tab */}
                <TabsContent value="jobs">
                  <div className="space-y-6">
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">{filteredJobs.length} job openings in {city}</p>
                      <Button className="bg-accent hover:bg-accent/90">
                        <Plus className="w-4 h-4 mr-2" />Post a Job
                      </Button>
                    </div>
                    {filteredJobs.map(job => (
                      <Card key={job.project_id || job.id} className="bg-card border-border hover:border-accent/50 transition-all">
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-xl font-semibold mb-2">{job.title}</h3>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-4 h-4" />{job.company}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />{job.location}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />{job.postedDate}
                                </span>
                              </div>
                            </div>
                            {job.urgent && (
                              <Badge className="bg-red-100 text-red-700 border-red-200">Urgent</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-4">{job.description}</p>
                          <div className="grid md:grid-cols-3 gap-4 mb-4 p-4 bg-muted/30 rounded-lg">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Budget</p>
                              <p className="font-semibold">{job.budget}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Duration</p>
                              <p className="font-semibold">{job.duration}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                              <p className="font-semibold">{job.startDate}</p>
                            </div>
                          </div>
                          <div className="mb-4">
                            <p className="text-sm font-medium mb-2">Requirements:</p>
                            <div className="flex flex-wrap gap-2">
                              {job.requirements.map((req, i) => (
                                <Badge key={`${job.id}-req-${i}`} variant="secondary" className="bg-accent/10 text-accent">
                                  <CheckCircle className="w-3 h-3 mr-1" />{req}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <Button className="w-full bg-accent hover:bg-accent/90" onClick={() => handleApplyJob(job)}>
                            Apply Now
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* VENDORS TAB */}
            <TabsContent value="vendors">
              <div className="space-y-8">
                {/* Vendor Listings */}
                <div>
                  <h3 className="text-2xl font-bold mb-6 text-foreground">Verified Vendors in {city}</h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {filteredVendors.map(vendor => (
                      <Card key={vendor.vendor_id || vendor.id} className="bg-card border-border hover:border-accent/50 transition-all">
                        <CardContent className="p-6">
                          <div className="flex gap-4">
                            {vendor.image ? (
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${vendor.image}`}
                                alt={vendor.vendor_name || vendor.name} 
                                className="w-24 h-24 rounded-lg object-cover border border-border"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className={`w-24 h-24 rounded-lg bg-accent/10 ${vendor.image ? 'hidden' : 'flex'} items-center justify-center text-3xl font-bold text-accent flex-shrink-0`}>
                              {(vendor.vendor_name || vendor.name).charAt(0)}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="text-lg font-semibold">{vendor.vendor_name || vendor.name}</h4>
                                  <p className="text-sm text-accent">{vendor.category}</p>
                                </div>
                                {vendor.verified && (
                                  <Badge className="bg-accent/10 text-accent border-accent/20">
                                    <Award className="w-3 h-3 mr-1" />Verified
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-sm mb-2">
                                {vendor.rating > 0 && (
                                  <div className="flex items-center gap-1">
                                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                    <span className="font-semibold">{vendor.rating.toFixed(1)}</span>
                                  </div>
                                )}
                                <span className="text-muted-foreground">{vendor.city}</span>
                              </div>
                              {vendor.description && <p className="text-sm text-muted-foreground mb-3">{vendor.description}</p>}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {(vendor.services || []).map((service, i) => (
                                  <Badge key={`${vendor.vendor_id || vendor.id}-service-${i}`} variant="secondary" className="text-xs">{service}</Badge>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleContactVendor(vendor)}>
                                  <Phone className="w-3 h-3 mr-1" />Call
                                </Button>
                                <Button size="sm" className="flex-1 bg-accent hover:bg-accent/90" onClick={() => navigate(`/vendor/${vendor.vendor_id}`)}>
                                  <Eye className="w-3 h-3 mr-1" />View
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Contribute Vendor Section */}
                <Card className="bg-gradient-to-br from-orange-50 to-background border-accent/30">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-2xl">
                      <Sparkles className="w-6 h-6 text-accent" />
                      Know a Great Vendor? List Them for FREE!
                    </CardTitle>
                    <p className="text-muted-foreground">
                      Help the community by adding vendors you&apos;ve worked with. No account needed!
                    </p>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleVendorSubmit} className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Vendor Name *</label>
                          <Input 
                            required
                            value={vendorFormData.name}
                            onChange={(e) => setVendorFormData({...vendorFormData, name: e.target.value})}
                            placeholder="e.g., Creative Props Studio"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Category *</label>
                          <Input 
                            required
                            value={vendorFormData.category}
                            onChange={(e) => setVendorFormData({...vendorFormData, category: e.target.value})}
                            placeholder="e.g., Props Rental"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Location *</label>
                          <Input 
                            required
                            value={vendorFormData.location}
                            onChange={(e) => setVendorFormData({...vendorFormData, location: e.target.value})}
                            placeholder="City name"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Phone</label>
                          <Input 
                            value={vendorFormData.phone}
                            onChange={(e) => setVendorFormData({...vendorFormData, phone: e.target.value})}
                            placeholder="+91 98765 43210"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Email</label>
                          <Input 
                            type="email"
                            value={vendorFormData.email}
                            onChange={(e) => setVendorFormData({...vendorFormData, email: e.target.value})}
                            placeholder="contact@vendor.com"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Services</label>
                          <Input 
                            value={vendorFormData.services}
                            onChange={(e) => setVendorFormData({...vendorFormData, services: e.target.value})}
                            placeholder="Comma separated services"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Description</label>
                        <Textarea 
                          value={vendorFormData.description}
                          onChange={(e) => setVendorFormData({...vendorFormData, description: e.target.value})}
                          placeholder="Brief description of the vendor and their services..."
                          rows={3}
                        />
                      </div>
                      <Button type="submit" className="w-full bg-accent hover:bg-accent/90">
                        <Plus className="w-4 h-4 mr-2" />
                        Submit Vendor for Review
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* PROP SHOPS TAB - Art Department Only */}
            {department === 'art-department' && (
              <TabsContent value="prop-shops">
                <div className="space-y-8">
                  <h3 className="text-2xl font-bold mb-6 text-foreground">Prop Shops & Rental Services in {city}</h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {[
                      {
                        id: 1,
                        name: 'Premium Props Studio',
                        category: 'Props & Set Pieces',
                        location: city,
                        rating: 4.9,
                        reviews: 120,
                        phone: '+91 98765 43213',
                        email: 'contact@premiumprops.com',
                        description: 'Largest collection of period and contemporary props. From vintage furniture to modern décor.',
                        services: ['Period Props', 'Furniture Rental', 'Custom Props', 'Decorative Items'],
                        image: 'https://images.unsplash.com/photo-1581093458791-9d42e3c5f4e7?w=400&h=300&fit=crop',
                        verified: true,
                        yearsInBusiness: 18
                      },
                      {
                        id: 2,
                        name: 'Artisan Prop House',
                        category: 'Handcrafted Props',
                        location: city,
                        rating: 4.7,
                        reviews: 85,
                        phone: '+91 98765 43214',
                        email: 'info@artisanprops.com',
                        description: 'Specialized in handcrafted custom props. Expert artisans for unique requirements.',
                        services: ['Custom Fabrication', 'Sculptures', 'Period Replicas', 'Consultation'],
                        image: 'https://images.unsplash.com/photo-1516876437184-593fda40c7ce?w=400&h=300&fit=crop',
                        verified: true,
                        yearsInBusiness: 12
                      }
                    ].map(shop => (
                      <Card key={shop.id} className="bg-card border-border hover:border-accent/50 transition-all">
                        <CardContent className="p-6">
                          <div className="flex gap-4">
                            <img src={shop.image} alt={shop.name} className="w-24 h-24 rounded-lg object-cover" />
                            <div className="flex-1">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="text-lg font-semibold">{shop.name}</h4>
                                  <p className="text-sm text-accent">{shop.category}</p>
                                </div>
                                {shop.verified && (
                                  <Badge className="bg-accent/10 text-accent border-accent/20">
                                    <Award className="w-3 h-3 mr-1" />Verified
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-sm mb-2">
                                <span className="text-muted-foreground">{shop.yearsInBusiness} years</span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-3">{shop.description}</p>
                              <div className="flex flex-wrap gap-2 mb-4">
                                {shop.services.map((service, i) => (
                                  <Badge key={`${shop.id}-service-${i}`} variant="secondary" className="text-xs">{service}</Badge>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleContactVendor(shop)}>
                                  <Phone className="w-3 h-3 mr-1" />Call
                                </Button>
                                <Button size="sm" className="flex-1 bg-accent hover:bg-accent/90" onClick={() => handleContactVendor(shop)}>
                                  <Mail className="w-3 h-3 mr-1" />Email
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Contribute Prop Shop Form */}
                  <Card className="bg-gradient-to-br from-orange-50 to-background border-accent/30">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-2xl">
                        <Sparkles className="w-6 h-6 text-accent" />
                        Know a Great Prop Shop? List It for FREE!
                      </CardTitle>
                      <p className="text-muted-foreground">
                        Help filmmakers find the best prop shops. No account required!
                      </p>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleVendorSubmit} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">Shop Name *</label>
                            <Input required placeholder="e.g., Premium Props Studio" />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Category *</label>
                            <Input required placeholder="e.g., Period Props" />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Location *</label>
                            <Input required placeholder="City name" />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Phone</label>
                            <Input placeholder="+91 98765 43210" />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Email</label>
                            <Input type="email" placeholder="contact@propshop.com" />
                          </div>
                          <div>
                            <label className="text-sm font-medium mb-2 block">Services</label>
                            <Input placeholder="Comma separated services" />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">Description</label>
                          <Textarea placeholder="Brief description of the prop shop..." rows={3} />
                        </div>
                        <Button type="submit" className="w-full bg-accent hover:bg-accent/90">
                          <Plus className="w-4 h-4 mr-2" />
                          Submit Prop Shop for Review
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-card mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center text-sm text-muted-foreground">
            <p>© 2024 Shoot Base. Built with passion for the film industry.</p>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {contactRecipient && (
        <ContactModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          recipient={contactRecipient}
        />
      )}
    </div>
  );
};

export default DepartmentDetailPage;
